import React from "react";

import { Home2026StatSubBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Mask, useReveal } from "../../shared/Reveal";
import { useCountUp } from "./useCountUp";

import styles from "./index.module.scss";

interface StatProps {
    value: Home2026StatSubBlock;
    numberShown: boolean;
    captionShown: boolean;
    ref?: React.Ref<HTMLLIElement>;
}

// `ref` is kept out of `props`: the compiler lint treats an object holding a
// ref as a ref, so reading any other prop off it would count as a ref read
const Stat = ({ ref, ...props }: StatProps) => {
    const to = props.value.number ?? 0;
    const count = useCountUp(to, props.numberShown);
    const hasNumber = props.value.number !== null;
    return (
        <li ref={ref} className={styles.stat}>
            {(hasNumber || props.value.suffix) && (
                <p
                    className={concatClassNames([
                        styles.num,
                        props.numberShown ? "" : styles.pre,
                    ])}
                >
                    {hasNumber && <span className={styles.value}>{count}</span>}
                    {props.value.suffix && (
                        <span className={styles.suffix}>
                            {props.value.suffix}
                        </span>
                    )}
                </p>
            )}
            {props.value.caption && (
                <p className={styles.caption}>
                    <Mask shown={props.captionShown}>
                        {props.value.caption}
                    </Mask>
                </p>
            )}
        </li>
    );
};

// proof.js: the second row follows the first by 260ms, stats within a row
// are 110ms apart, and each caption follows its number by 90ms
const ROW_DELAY = 260;
const STAT_DELAY = 110;
const CAPTION_DELAY = 90;

interface StatRowProps {
    row: number;
    stats: { id: string; value: Home2026StatSubBlock }[];
}

/**
 * One row of stats with its own scroll gate, measured on the row's first
 * stat. Renders its stats straight into the parent grid.
 */
export const StatRow = (props: StatRowProps) => {
    const { ref, shown } = useReveal<HTMLLIElement>({
        enter: 0.84,
        exit: 0.98,
        delays: props.stats.flatMap((_, i) => {
            const at = props.row * ROW_DELAY + i * STAT_DELAY;
            return [at, at + CAPTION_DELAY];
        }),
    });
    return (
        <>
            {props.stats.map(({ id, value }, i) => (
                <Stat
                    key={id}
                    ref={i === 0 ? ref : undefined}
                    value={value}
                    numberShown={shown[i * 2]}
                    captionShown={shown[i * 2 + 1]}
                />
            ))}
        </>
    );
};
